<template>
  <div class="sign-in">
    <SignBanner result="auth" />
    <ModalComponentBody>
      <SignTitle>
        {{ messages?.entry }}
      </SignTitle>
      <SignText>
        {{ messages?.authorizationText }}
      </SignText>
      <form class="sign-in-form" @submit.prevent="handlerSubmit">
        <DefPhone
          :initial-country="initialCountry"
          :placeholder="messages?.phoneNumber"
          name="phone"
          required
          :is-show-error="Boolean(errors?.signInByPhone)"
          @countrychange="countrychange"
        >
          {{ errors?.signInByPhone }}
        </DefPhone>
        <DefInputPassword
          v-if="!isUkrainePhone"
          :placeholder="messages?.password"
          name="password"
          :is-show-error="Boolean(errors && errors.signInByPhoneAndPassword)"
          required
        >
          {{ errors?.signInByPhoneAndPassword }}
        </DefInputPassword>

        <ForgetPasswordBtn v-if="!isUkrainePhone" />

        <DefButtonPrimary type="submit"> {{ messages?.enter }} </DefButtonPrimary>
        <GoSignUp />
      </form>
    </ModalComponentBody>
  </div>
</template>

<script setup lang="ts">
import SignBanner from '@components/Cabinet/Entry/SignBanner.vue';
import ModalComponentBody from '@use/useModal/ModalComponentBody.vue';
import SignTitle from '@components/Cabinet/Entry/SignTitle.vue';
import SignText from '@components/Cabinet/Entry/SignText.vue';
import { useStore } from '@/store';
import { computed, onBeforeMount, reactive } from 'vue';
import DefPhone from '@components/Def/DefPhone/DefPhone.vue';
import DefInputPassword from '@components/Def/DefInput/DefInputPassword.vue';
import DefButtonPrimary from '@components/Def/DefButtonPrimary/DefButtonPrimary.vue';
import GoSignUp from '@components/Cabinet/Entry/SignIn/GoSignUp.vue';
import Recaptcha3 from '@/utils/Recaptcha3';
import ForgetPasswordBtn from '@components/Cabinet/Entry/ForgotPassword/ForgotPasswordBtn.vue';
import SignInConfirmPhone from '@components/Cabinet/Entry/SignIn/SignInConfirmPhone.vue';
import useModal from '@use/useModal/useModal';

const { state, dispatch, getters } = useStore();
const modal = useModal();

const auth = computed(() => state.personal.data?.routes?.auth.data);
const messages = computed(() => state.results?.messages?.entry);
const errors = computed(() => state.personal.data?.errors);
const signInBanners = computed(() => state.results?.signInBanners);

const getData = async () => {
  const { action, api } = auth.value;
  await dispatch('personal/getData', {
    action,
    api,
  });
};

const getBanner = () => {
  signInBanners.value?.find((el) => {
    if (el.signInTypeKeys.find((key) => key === 'auth')) {
      return true;
    }

    return false;
  });
};
const getSelectedCountry = computed(() => getters.getSelectedCountry);
const initialCountry = computed(() => {
  if (getSelectedCountry.value?.UF_COUNTRY_ISO_CODE) {
    return getSelectedCountry.value?.UF_COUNTRY_ISO_CODE;
  }
  return undefined;
});

const componentState = reactive({
  selectedCountryCode: '',
});

const isUkrainePhone = computed(() => {
  if (componentState.selectedCountryCode === '' && initialCountry.value?.toLowerCase() === 'ua') {
    return true;
  }
  return componentState.selectedCountryCode === 'ua';
});

const countrychange = (countryCode: string) => {
  if (countryCode) {
    componentState.selectedCountryCode = countryCode.toLowerCase();
  }
};

const handlerSubmit = async (e: SubmitEvent): void => {
  const formData: FormData = new FormData(e.target as HTMLFormElement);

  const token = await Recaptcha3.getSubmitToken();
  formData.append('g-recaptcha-response', token);

  if (isUkrainePhone.value) {
    // console.log('UKRAINE');
    const response = await dispatch('personal/signInByPhone', formData);

    if (response) {
      modal.show(
        SignInConfirmPhone,
        { result: { formData } },
        {
          width: '900px',
          backgroundClose: false,
        }
      );
    }
  } else {
    // console.log('International');
    const response = await dispatch('personal/login_by_phone_and_password', formData);

    if (response?.status === true) {
      window.location.reload();
    }
  }
};

onBeforeMount(async () => {
  await getData();
  getBanner();
});
</script>

<style lang="scss" scoped>
.sign-in {
  @include point-md {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  :deep(.def-button-primary) {
    margin-top: 4px;
  }
}
.sign-in-form {
  display: grid;
  grid-row-gap: 12px;
}
</style>
